import { useCallback, useEffect, useState } from "react";
import { Bug, Lightbulb, MessageSquarePlus, Send, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

export const FEEDBACK_STATUS_META = {
  nieuw: { label: "Nieuw", cls: "bg-blue-100 text-blue-700" },
  in_behandeling: { label: "In behandeling", cls: "bg-amber-100 text-amber-700" },
  hertesten: { label: "Hertesten", cls: "bg-purple-100 text-purple-700" },
  opgelost: { label: "Opgelost", cls: "bg-emerald-100 text-emerald-700" },
  afgewezen: { label: "Afgewezen", cls: "bg-gray-200 text-gray-600" },
};

export const FeedbackComments = ({ item, onCommentAdded }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/feedback/${item.id}/comments`, { text: text.trim() });
      setText("");
      onCommentAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2 mt-2">
      {(item.comments || []).map((c) => (
        <div key={c.id} data-testid={`feedback-comment-${c.id}`}
          className={`rounded-xl px-3 py-2 text-sm ${c.author_role === "admin" ? "bg-primary/10" : "bg-secondary"}`}>
          <p className="leading-relaxed">{c.text}</p>
          <p className="text-[11px] text-muted-foreground mt-1 font-semibold">
            {c.author_name}{c.author_role === "admin" && <span className="ml-1.5 text-[10px] font-bold uppercase text-primary">VoltRoute-team</span>}
            <span className="font-mono font-normal"> · {new Date(c.created_at).toLocaleString("nl-BE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          </p>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <input data-testid={`feedback-comment-input-${item.id}`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} maxLength={2000} placeholder="Schrijf een reactie..."
          className="flex-1 min-w-0 bg-secondary rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
        <button data-testid={`feedback-comment-send-${item.id}`} onClick={send} disabled={!text.trim() || busy}
          className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 shrink-0 active:scale-95 transition-[transform] duration-200">
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const FeedbackItem = ({ item, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const st = FEEDBACK_STATUS_META[item.status] || FEEDBACK_STATUS_META.nieuw;
  return (
    <div data-testid={`feedback-item-${item.id}`} className="bg-card border border-border rounded-2xl p-3.5">
      <button className="w-full flex items-center justify-between gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid={`feedback-item-toggle-${item.id}`}>
        <div className="flex items-center gap-2 min-w-0">
          {item.type === "bug" ? <Bug className="w-4 h-4 text-red-500 shrink-0" /> : <Lightbulb className="w-4 h-4 text-amber-500 shrink-0" />}
          <p className="font-semibold text-sm truncate">{item.title}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span data-testid={`feedback-status-${item.id}`} className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 ${st.cls}`}>{st.label}</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>
      {open && (
        <div className="mt-2">
          <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
          <p className="text-[11px] text-muted-foreground font-mono mt-1">Gemeld op {new Date(item.created_at).toLocaleDateString("nl-BE")}</p>
          <FeedbackComments item={item} onCommentAdded={onRefresh} />
        </div>
      )}
    </div>
  );
};

export const FeedbackWidget = () => {
  const { user } = useAuth() || {};
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("melden");
  const [items, setItems] = useState([]);
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/config/public").then((r) => setEnabled(!!r.data.feedback_enabled)).catch(() => {});
  }, [user]);

  const load = useCallback(() => {
    api.get("/feedback").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!user || !enabled) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) return toast.error("Geef een korte titel (min. 3 tekens)");
    if (description.trim().length < 3) return toast.error("Beschrijf je melding iets uitgebreider");
    setBusy(true);
    try {
      await api.post("/feedback", { type, title: title.trim(), description: description.trim() });
      toast.success(type === "bug" ? "Bug gemeld — bedankt! Je volgt de status in 'Mijn meldingen'." : "Suggestie doorgestuurd — bedankt!");
      setTitle("");
      setDescription("");
      load();
      setTab("archief");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="feedback-widget-button" onClick={() => setOpen(true)} title="Feedback of bug melden"
        style={{ writingMode: "vertical-rl" }}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-40 rounded-r-xl bg-[#B5838D] hover:bg-[#8E5D67] text-white text-xs font-bold tracking-widest uppercase px-1.5 py-4 shadow-lg rotate-180 transition-[background-color] duration-200">
        Feedback
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><MessageSquarePlus className="w-5 h-5 text-[#B5838D]" /> Feedback & meldingen</DialogTitle>
            <DialogDescription className="text-xs">Meld een bug of deel een suggestie — je volgt de status en reacties in je archief.</DialogDescription>
          </DialogHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-full w-full">
              <TabsTrigger data-testid="feedback-tab-melden" value="melden" className="rounded-full flex-1 text-xs">Melden</TabsTrigger>
              <TabsTrigger data-testid="feedback-tab-archief" value="archief" className="rounded-full flex-1 text-xs">Mijn meldingen ({items.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="melden">
              <form onSubmit={submit} className="space-y-3 pt-2">
                <div className="flex gap-2">
                  <button type="button" data-testid="feedback-type-bug" onClick={() => setType("bug")}
                    className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border transition-[background-color,border-color] duration-200 ${type === "bug" ? "bg-red-50 border-red-300 text-red-600 dark:bg-red-950/40" : "bg-secondary border-transparent"}`}>
                    <Bug className="w-4 h-4" /> Bug
                  </button>
                  <button type="button" data-testid="feedback-type-suggestie" onClick={() => setType("suggestie")}
                    className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 border transition-[background-color,border-color] duration-200 ${type === "suggestie" ? "bg-amber-50 border-amber-300 text-amber-600 dark:bg-amber-950/40" : "bg-secondary border-transparent"}`}>
                    <Lightbulb className="w-4 h-4" /> Suggestie
                  </button>
                </div>
                <Input data-testid="feedback-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                  placeholder={type === "bug" ? "bv. Kaart laadt niet op dag 3" : "bv. Filter laadstops op vermogen"} className="rounded-xl h-11" />
                <Textarea data-testid="feedback-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={2000}
                  placeholder={type === "bug" ? "Wat ging er mis? Wat verwachtte je? Op welke pagina?" : "Beschrijf je idee..."} className="rounded-xl" />
                <Button data-testid="feedback-submit-button" type="submit" disabled={busy} className="w-full rounded-full bg-[#B5838D] hover:bg-[#8E5D67]">
                  {busy ? "Versturen..." : "Verstuur melding"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="archief">
              <div className="space-y-2 pt-2" data-testid="feedback-archive-list">
                {items.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nog geen meldingen — meld gerust een bug of idee!</p>}
                {items.map((it) => <FeedbackItem key={it.id} item={it} onRefresh={load} />)}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
